import { useCallback, useEffect, useRef, useState } from 'react';
import { apiRequest } from '../services/apiClient';

/**
 * Tenant-safe data hook for the Employee Directory.
 *
 * Owns one outstanding fetch (AbortController), tracks filter state,
 * and exposes a `loadMore` helper that walks the opaque cursor. The
 * caller drives filter updates by passing in a stable `filters`
 * object — when it changes (shallow), pagination resets to page 1.
 */
function buildQuery(filters, cursor) {
  const params = new URLSearchParams();
  if (filters?.q) params.set('q', filters.q);
  if (filters?.employmentType) params.set('employmentType', filters.employmentType);
  if (filters?.supervisorEmployeeId) params.set('supervisorEmployeeId', String(filters.supervisorEmployeeId));
  // Workforce People Model Phase 3.2 — classification + department.
  if (filters?.employmentClassification) params.set('employmentClassification', filters.employmentClassification);
  if (filters?.department) params.set('department', filters.department);
  if (filters?.locationId) params.set('locationId', String(filters.locationId));
  if (filters?.archived) params.set('archived', filters.archived);
  if (filters?.limit) params.set('limit', String(filters.limit));
  if (cursor) params.set('cursor', cursor);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useEmployeeDirectory(filters = {}) {
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('loading'); // 'loading' | 'loaded' | 'error' | 'forbidden'
  const abortRef = useRef(null);

  const filtersKey = JSON.stringify({
    q: filters.q || '',
    employmentType: filters.employmentType || '',
    supervisorEmployeeId: filters.supervisorEmployeeId || '',
    employmentClassification: filters.employmentClassification || '',
    department: filters.department || '',
    locationId: filters.locationId || '',
    archived: filters.archived || 'false',
    limit: filters.limit || 25
  });

  const fetchPage = useCallback(async (cursor = null, { append = false } = {}) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);
    setError('');
    try {
      const data = await apiRequest(`/api/employees/profiles${buildQuery(filters, cursor)}`, {
        signal: controller.signal
      });
      if (controller.signal.aborted) return;
      setItems(prev => append ? [...prev, ...(data.items || [])] : (data.items || []));
      setNextCursor(data.nextCursor || null);
      setStatus('loaded');
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err?.status === 403) {
        setStatus('forbidden');
        setError(err.message || 'You do not have permission to view the directory.');
        return;
      }
      setStatus('error');
      setError(err?.message || 'Unable to load the employee directory.');
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  useEffect(() => {
    fetchPage(null);
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!nextCursor || isLoading) return;
    fetchPage(nextCursor, { append: true });
  }, [nextCursor, isLoading, fetchPage]);

  const refetch = useCallback(() => fetchPage(null), [fetchPage]);

  return { items, nextCursor, isLoading, error, status, loadMore, refetch };
}
